import React, { PureComponent } from 'react';
import map from 'lodash/map';
import noop from 'lodash/noop';
import classNames from 'classnames';

import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eb/eds-icon';
import { HelpChunky } from '@eb/eds-iconography';
import { NewWindowChunky } from '@eb/eds-iconography';
import { Guidance } from '@eb/eds-guidance';
import {
    CoyoteProgressIndicator,
    EventActionBarWithOutHOC as EventPageActionBar,
} from '@eb/event-create-primitives';
import { translationPropType } from '@eventbrite/i18n';
import { SIZE_BLOCK, STYLE_FILL, STYLE_NEUTRAL } from '@eventbrite/eds-button';

import transform from '../transform';
import defaultSettings from './settings';
import {
    GA_CHANGE_PLAN_CATEGORY,
    GA_CHOOSE_PACKAGE,
    GA_DOWNGRADE_PAGE_VIEW,
    GA_P2_LABEL,
    GA_PACKAGE_DOWNGRADED,
} from '../../constants/analytics';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Heading from '../../components/Heading';
import { isActiveForAU, loadCrazyEgg, trackAnalyticsEventAU } from '../utils';
import { CONFLICTS_TRANSLATIONS } from '../../constants/downgrade';

import './page.scss';

const LINK_CONFIG = {
    linkText: CONFLICTS_TRANSLATIONS.table.guidanceTextLink,
    linkTo: CONFLICTS_TRANSLATIONS.table.guidanceLink,
    linkIcon: <Icon type={<NewWindowChunky />} size="xsmall" color="ui-blue" />,
    onLinkClick: noop,
};

const FeatureTableHeaderTitle = ({ title, subTitle }) => {
    let subTitleMarkup;

    if (subTitle) {
        subTitleMarkup = (
            <p className="eds-text-bm eds-text-weight--light eds-show-up-mn">
                {subTitle}
            </p>
        );
    }

    return (
        <div className="eds-text--center">
            <p className="eds-text-bl eds-text-weight--heavy eds-show-up-md">
                {title}
            </p>
            <p className="eds-text-bm eds-show-down-mn">{title}</p>
            {subTitleMarkup}
        </div>
    );
};

FeatureTableHeaderTitle.propTypes = {
    title: translationPropType,
    subTitle: translationPropType,
};

FeatureTableHeaderTitle.defaultProps = {
    title: '',
    subTitle: '',
};

const FeatureTableRowItem = ({ children, highlightColumn }) => {
    const dynamicProps = {
        className: 'feature-table__row-item eds-l-pad-vert-4',
    };

    if (!children) {
        dynamicProps['dangerouslySetInnerHTML'] = {
            __html: '&mdash;',
        };
        return <div {...dynamicProps}>{children}</div>;
    }

    const highlightColumnClass = classNames({
        'feature-table__row-item_border': highlightColumn,
    });

    return (
        <div {...dynamicProps}>
            <div className={highlightColumnClass} />
            {children}
        </div>
    );
};

FeatureTableRowItem.defaultProps = {
    children: null,
    highlightColumn: false,
};

const FeatureTableRow = ({
    isHeader,
    feature,
    currentPackage,
    newPackage,
    highlightColumn,
}) => {
    const classes = classNames('feature-table__row', {
        'feature-table__header': isHeader,
    });

    return (
        <div className={classes}>
            <FeatureTableRowItem>{feature}</FeatureTableRowItem>
            <FeatureTableRowItem highlightColumn={highlightColumn}>
                {currentPackage}
            </FeatureTableRowItem>
            <FeatureTableRowItem>{newPackage}</FeatureTableRowItem>
        </div>
    );
};

const FeatureTableContents = ({ features }) => {
    const rows = map(
        features,
        ({ feature, currentPackage, newPackage }, key) => (
            <FeatureTableRow
                key={key}
                feature={feature}
                currentPackage={currentPackage}
                newPackage={newPackage}
            />
        ),
    );

    return <div>{rows}</div>;
};

const FeatureTable = ({
    features,
    highlightProfessional,
    currentPackageTitle,
    currentPackageSubtitle,
    newPackageTitle,
    newPackageSubtitle,
    tableTitle,
    tableTitleShort,
}) => {
    const featuresTitle = (
        <div>
            <div className="eds-show-down-mn">
                <FeatureTableHeaderTitle title={tableTitleShort} />
            </div>
            <div className="eds-show-up-md">
                <FeatureTableHeaderTitle title={tableTitle} />
            </div>
        </div>
    );
    const currentTitle = (
        <FeatureTableHeaderTitle
            title={currentPackageTitle}
            subTitle={currentPackageSubtitle}
        />
    );
    const newTitle = (
        <FeatureTableHeaderTitle
            title={newPackageTitle}
            subTitle={newPackageSubtitle}
        />
    );
    const tableWrapperClasses = classNames(
        'eds-g-group',
        'eds-l-pad-vert-14',
        'eds-l-pad-hor-4',
        {
            'feature-table__wrapper': highlightProfessional,
        },
    );

    return (
        <div className={tableWrapperClasses}>
            <div className="eds-g-cell eds-g-cell-1-1">
                <div className="feature-table eds-g-cell eds-g-cell-1-1">
                    <FeatureTableRow
                        isHeader={true}
                        feature={featuresTitle}
                        currentPackage={currentTitle}
                        newPackage={newTitle}
                        highlightColumn={highlightProfessional}
                    />
                    <FeatureTableContents features={features} />
                </div>
            </div>
        </div>
    );
};

const ChangeCTA = ({
    onChangePackage,
    onKeepPackage,
    keepPackageCTA,
    changePackageCTA,
    isLoading,
}) => {
    const buttonWrapperClassName = classNames(
        'eds-g-cell',
        'eds-g-cell',
        'eds-g-cell-1-1',
        'eds-g-cell-sw-1-2',
        'eds-g-cell-md-4-12',
        'eds-g-cell-mw-3-12',
        'eds-l-pad-hor-2',
        'eds-l-pad-vert-2',
    );
    return (
        <div className="eds-g-group eds-l-pad-vert-14 eds-text--center">
            <div className={buttonWrapperClassName}>
                <Button
                    onClick={onKeepPackage}
                    size={SIZE_BLOCK}
                    style={STYLE_FILL}
                    data-spec="downgrade-cancellation-button"
                >
                    {keepPackageCTA}
                </Button>
            </div>
            <div className={buttonWrapperClassName}>
                <Button
                    size={SIZE_BLOCK}
                    style={STYLE_NEUTRAL}
                    onClick={onChangePackage}
                    data-spec="downgrade-confirmation-button"
                    isLoading={isLoading}
                >
                    {changePackageCTA}
                </Button>
            </div>
        </div>
    );
};

const ActionBarComponent = ({
    onKeepPackage,
    onChangePackage,
    useStandardActionBar,
    submitText,
    discardText,
    isLoading,
}) =>
    useStandardActionBar ? (
        <div className="eds-l-pad-top-14">
            <EventPageActionBar
                visible={true}
                onDiscard={onKeepPackage}
                onSave={onChangePackage}
                submitText={submitText}
                discardText={discardText}
                isLoading={isLoading}
            />
        </div>
    ) : (
        <ChangeCTA
            onKeepPackage={onKeepPackage}
            onChangePackage={onChangePackage}
            keepPackageCTA={discardText}
            changePackageCTA={submitText}
            isLoading={isLoading}
        />
    );

export default class ConfirmPlanChangePage extends PureComponent {
    state = {
        tableIsLoading: true,
        conflictsIsLoading: false,
    };

    componentDidMount() {
        const { trackEvent } = this.props;

        trackEvent({
            action: GA_DOWNGRADE_PAGE_VIEW,
            label: GA_P2_LABEL,
            category: GA_CHANGE_PLAN_CATEGORY,
        });

        this._trackEventActionsAU();

        //Crazy Egg
        loadCrazyEgg();

        this.setState({ tableIsLoading: false });
    }
    _trackEventActionsAU() {
        const {
            assortmentsAvailability,
            locale,
            userId,
            userPlan,
        } = this.props;

        const version = assortmentsAvailability
            ? assortmentsAvailability.current.version
            : null;
        if (isActiveForAU(locale)) {
            trackAnalyticsEventAU({
                action: GA_PACKAGE_DOWNGRADED,
                category: GA_CHOOSE_PACKAGE,
                dimensions: {
                    userId: userId,
                    userPlan: userPlan,
                    version: version,
                },
            });
        }
    }
    _handleChangePackage = () => {
        const { onChangePackage } = this.props;
        this.setState({ conflictsIsLoading: true });
        onChangePackage().catch(() =>
            this.setState({ conflictsIsLoading: false }),
        );
    };

    render() {
        const {
            breadcrumb,
            isPackageChangeDowngradeEnabled,
            changePackageCTA,
            currentPackageSubtitle,
            currentPackageTitle,
            features,
            firstHeading,
            keepPackageCTA,
            newPackageSubtitle,
            newPackageTitle,
            onKeepPackage,
            tableTitle,
            tableTitleShort,
        } = this.props;
        const { tableIsLoading, conflictsIsLoading } = this.state;

        const { breadcrumbs } = transform({ defaultSettings });
        const crumbs = [...breadcrumbs.crumbs, breadcrumb];

        if (tableIsLoading) {
            return <CoyoteProgressIndicator />;
        }

        return (
            <div>
                <HeaderBreadcrumbs crumbs={crumbs} />
                <div className="eds-g-grid marketing-container__max-width--wide">
                    <Heading {...firstHeading} />
                    <FeatureTable
                        currentPackageTitle={currentPackageTitle}
                        currentPackageSubtitle={currentPackageSubtitle}
                        newPackageTitle={newPackageTitle}
                        newPackageSubtitle={newPackageSubtitle}
                        tableTitle={tableTitle}
                        tableTitleShort={tableTitleShort}
                        highlightProfessional={isPackageChangeDowngradeEnabled}
                        features={features}
                    />
                    <div className="eds-g-cell eds-l-pad-left-3">
                        <Guidance
                            data-spec="downgrade-guidance"
                            textContent={
                                CONFLICTS_TRANSLATIONS.table.guidanceText
                            }
                            linkConfig={LINK_CONFIG}
                            primaryIcon={<Icon type={<HelpChunky />} />}
                        />
                    </div>
                    <ActionBarComponent
                        onKeepPackage={onKeepPackage}
                        onChangePackage={this._handleChangePackage}
                        useStandardActionBar={isPackageChangeDowngradeEnabled}
                        submitText={changePackageCTA}
                        discardText={keepPackageCTA}
                        isLoading={conflictsIsLoading}
                    />
                </div>
            </div>
        );
    }
}
